import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { resolvePath, pathFor } from "../config/routeSlugs";

/**
 * Sprint 119.39 — Wrapper de ruta que força un idioma específic.
 * Usat per rutes prefixades: /es/foo, /fr/foo, /de/foo, /it/foo, /en/foo.
 *
 * Objectiu SEO: cada idioma té la seva URL canònica pròpia, cosa que
 * permet a Google indexar correctament i implementar hreflang.
 *
 * Uso:
 *   <Route path="/fr/ficha-tecnica" element={
 *     <LangRoute lang="fr"><PassportGenerator /></LangRoute>
 *   } />
 */
const LangRoute = ({ lang, children }) => {
  const { i18n } = useTranslation();

  useEffect(() => {
    if (lang && i18n.language !== lang) {
      i18n.changeLanguage(lang);
    }
  }, [lang, i18n]);

  return children;
};

// Idiomes suportats pel SEO multi-idioma
// Sprint 119.97: NL afegit com a 6è idioma (GSC 2026-08-12).
export const SUPPORTED_LANGS = ["es", "en", "fr", "de", "it", "nl"];

/**
 * Rutes que EXISTEIXEN amb prefix d'idioma (/{lang}/<ruta>).
 * ==================================================================
 * Una sola llista, perquè fins ara la informació estava repartida i les
 * peces no coincidien: App.js declarava les rutes, server.js en tenia
 * una còpia en un comentari, i el hub enllaçava sense prefix. Resultat:
 * des de /nl/calculadoras clicaves una targeta i queies en castellà.
 *
 * Qualsevol ruta que NO sigui aquí, amb prefix rep un 301 del servidor
 * cap a la canònica sense prefix. Per això afegir una ruta prefixada són
 * SEMPRE dues coses alhora: entrada aquí + `<Route>` a App.js. Si només
 * es fa una de les dues, o bé l'idioma es perd o bé surt pàgina blanca.
 *
 * La terrassa NO hi és a propòsit: només és vàlida en castellà.
 */
export const PREFIXED_ROUTES = [
  "", // l'arrel: /{lang}
  "ficha-tecnica",
  "initiative",
  "instaladores",
  "solicitar-sello",
  "pool-check",
  "calculadoras",
  "why-passport",
  "early-access",
  "documentar-piscina",
  // Sprint 148 — El blog entra al menú superior. Les rutes /{lang}/blog
  // existeixen a App.js des del Sprint 119.97, però no eren aquí, així que
  // localizePath les deixava sense prefix i qualsevol enllaç intern al
  // blog et treia de l'idioma.
  "blog",
  "piscina-legal",
  "comprar-casa-con-piscina",
  // Sprint 154 — Les calculadores han sortit d'aquesta llista: el seu slug
  // ja no és el mateix a totes les llengües i les resol routeSlugs.js.
];

const PREFIXED_SET = new Set(PREFIXED_ROUTES);

/**
 * Afegeix el prefix d'idioma a una ruta interna, si i només si aquella
 * ruta existeix prefixada i l'idioma no és el castellà (que és la
 * canònica sense prefix).
 *
 * Si la ruta no existeix prefixada retorna l'original: val més perdre
 * l'idioma que enviar l'usuari a una URL que no existeix.
 */
export function localizePath(path, lang) {
  if (!path || !path.startsWith("/")) return path;
  if (!lang || !SUPPORTED_LANGS.includes(lang)) return path;
  // Sprint 154 — Seccions amb slug per idioma: la traducció de la ruta no és
  // posar-hi un prefix, és canviar el slug sencer. /de/poolvolumen-berechnen
  // no s'obté de /tools/pool-volume-calculator afegint-hi "/de".
  // Va primer perquè aquí el castellà TAMPOC és el cas neutre: la calculadora
  // de volum té l'anglès com a idioma sense prefix.
  const hit = resolvePath(path);
  if (hit) {
    const cut = path.search(/[?#]/);
    const query = cut === -1 ? "" : path.slice(cut);
    const target = pathFor(hit.key, lang);
    return target ? target + query : path;
  }
  if (lang === "es") return path;
  // La ruta pot portar query (?v=48&u=m3) o fragment: el prefix va al
  // camí, la resta es conserva intacta.
  const cut = path.search(/[?#]/);
  const bare = cut === -1 ? path : path.slice(0, cut);
  const query = cut === -1 ? "" : path.slice(cut);
  const clean = bare.replace(/^\//, "").replace(/\/$/, "");
  if (!PREFIXED_SET.has(clean)) return path;
  // L'arrel es "" a la llista: sense aquest cas donava "/de/" amb barra
  // final, que es una URL diferent de "/de" per a un rastrejador.
  return clean ? `/${lang}/${clean}${query}` : `/${lang}${query}`;
}

// Helper per generar hreflang alternates d'una URL neutra
// Ex: hreflangAlternates("/ficha-tecnica") →
//   { es: "/es/ficha-tecnica", en: "/en/ficha-tecnica", ... }
export function hreflangAlternates(neutralPath) {
  const clean = neutralPath.startsWith("/") ? neutralPath : `/${neutralPath}`;
  const out = { "x-default": clean };
  SUPPORTED_LANGS.forEach((l) => {
    out[l] = `/${l}${clean === "/" ? "" : clean}`;
  });
  return out;
}

export default LangRoute;
